import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Search, Briefcase, Users, ArrowRight, CheckCircle, Mail } from 'lucide-react';

export default function LandingPage() {
  const { user, login } = useAuth();
  
  // If user is logged in, show redirect options
  if (user) {
    const dashboardPath = user.role === 'researcher' 
      ? '/researcher/dashboard' 
      : '/client/dashboard';
    window.location.href = dashboardPath;
    return null;
  }
  
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="header-glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-neutral-900 rounded-sm flex items-center justify-center text-white font-bold text-sm">
              RE
            </div>
            <span className="font-bold text-xl" style={{ fontFamily: 'Outfit' }}>
              Research Exchange
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#how-it-works" className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">
              How it Works
            </a>
            <a href="#features" className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">
              Features
            </a>
            <a href="/about" className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">
              About Us
            </a>
          </nav>
        </div>
      </header>
      
      {/* Hero Section - Split Design */}
      <section className="pt-24 min-h-screen flex flex-col lg:flex-row">
        {/* Researcher Side (Orange) */}
        <div className="flex-1 relative overflow-hidden">
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1761392676464-2d518ffa243d?w=1200')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#FFF0EB] to-white" />
          
          <div className="relative z-10 h-full flex flex-col justify-center p-8 lg:p-16">
            <span className="text-xs font-bold uppercase tracking-wider text-[#FF6B35] mb-4">
              For Researchers
            </span>
            <h1 
              className="text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight mb-6"
              style={{ fontFamily: 'Outfit' }}
            >
              Discover Gigs,<br />
              <span className="text-[#FF6B35]">Briefs & Contracts</span>
            </h1>
            <p className="text-lg text-neutral-600 mb-8 max-w-md">
              Your marketplace for research work — from one-off projects and 
              consulting gigs to long-term contracts and RFP responses.
            </p>
            
            <ul className="space-y-3 mb-8">
              {['Build a profile that showcases your expertise', 'Browse gigs, contracts & project briefs', 'Express interest, get shortlisted, win work'].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-neutral-700">
                  <CheckCircle className="text-[#FF6B35]" size={20} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            
            <button
              data-testid="join-as-researcher-btn"
              onClick={() => login('researcher')}
              className="btn-researcher px-8 py-4 font-semibold text-lg w-fit flex items-center gap-2 hover-lift"
            >
              Join as Researcher
              <ArrowRight size={20} />
            </button>
            <Link to="/auth/email?role=researcher" className="text-sm text-neutral-500 hover:text-[#FF6B35] flex items-center gap-1.5 mt-3">
              <Mail size={14} /> Or sign in with email
            </Link>
          </div>
        </div>
        
        {/* Divider */}
        <div className="hidden lg:block w-px bg-neutral-200" />
        
        {/* Client Side (Teal) */}
        <div className="flex-1 relative overflow-hidden">
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1770522297890-da71a8ba0084?w=1200')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#E6F3F4] to-white" />
          
          <div className="relative z-10 h-full flex flex-col justify-center p-8 lg:p-16">
            <span className="text-xs font-bold uppercase tracking-wider text-[#0D7377] mb-4">
              For Clients
            </span>
            <h1 
              className="text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight mb-6"
              style={{ fontFamily: 'Outfit' }}
            >
              Commission<br />
              <span className="text-[#0D7377]">Research, Your Way</span>
            </h1>
            <p className="text-lg text-neutral-600 mb-8 max-w-md">
              Post project briefs, gigs, or contracts. Browse verified researchers 
              and agencies. Find the right expertise for every engagement.
            </p>
            
            <ul className="space-y-3 mb-8">
              {['Post briefs, gigs, contracts & RFPs', 'Browse researcher & agency profiles', 'Shortlist, select and manage responses'].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-neutral-700">
                  <CheckCircle className="text-[#0D7377]" size={20} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            
            <button
              data-testid="join-as-client-btn"
              onClick={() => login('client')}
              className="btn-client px-8 py-4 font-semibold text-lg w-fit flex items-center gap-2 hover-lift"
            >
              Join as Client
              <ArrowRight size={20} />
            </button>
            <Link to="/auth/email?role=client" className="text-sm text-neutral-500 hover:text-[#0D7377] flex items-center gap-1.5 mt-3">
              <Mail size={14} /> Or sign in with company email
            </Link>
          </div>
        </div>
      </section>
      
      {/* How it Works */}
      <section id="how-it-works" className="py-24 sm:py-32 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-4 block">
              How it Works
            </span>
            <h2 
              className="text-3xl sm:text-4xl font-bold tracking-tight"
              style={{ fontFamily: 'Outfit' }}
            >
              Simple, Efficient, Effective
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: 'Create Your Profile',
                desc: 'Showcase your methods, sectors, tools, and certifications. Add agency details and get verified.',
                color: '#FF6B35'
              },
              {
                icon: Search,
                title: 'Discover & Match',
                desc: 'Researchers explore briefs, gigs and contracts. Clients browse profiles and post requirements.',
                color: '#0D7377'
              },
              {
                icon: Briefcase,
                title: 'Engage & Deliver',
                desc: 'Express interest, get shortlisted, and start working — from quick projects to ongoing partnerships.',
                color: '#212529'
              }
            ].map((step, i) => (
              <div 
                key={i}
                className="bg-white p-8 border border-neutral-200 hover-lift"
              >
                <div 
                  className="w-12 h-12 rounded-sm flex items-center justify-center mb-6"
                  style={{ backgroundColor: step.color }}
                >
                  <step.icon className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>
                  {step.title}
                </h3>
                <p className="text-neutral-600">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features */}
      <section id="features" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Researcher Features */}
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#FF6B35] mb-4 block">
                Researcher Features
              </span>
              <h2 
                className="text-2xl sm:text-3xl font-bold tracking-tight mb-8"
                style={{ fontFamily: 'Outfit' }}
              >
                Tools to Win More Work
              </h2>
              <ul className="space-y-4">
                {[
                  'Deep profile — methods, sectors, tools & languages',
                  'Browse gigs, contracts, briefs & RFPs',
                  'Express interest in briefs, apply to opportunities',
                  'Save listings and track applications',
                  'Direct messaging with clients'
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-sm bg-[#FFF0EB] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="text-[#FF6B35]" size={14} />
                    </div>
                    <span className="text-neutral-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Client Features */}
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#0D7377] mb-4 block">
                Client Features
              </span>
              <h2 
                className="text-2xl sm:text-3xl font-bold tracking-tight mb-8"
                style={{ fontFamily: 'Outfit' }}
              >
                Source Expertise, Effortlessly
              </h2>
              <ul className="space-y-4">
                {[
                  'Post briefs, gigs, contracts or RFPs',
                  'Upload detailed brief documents for download',
                  'Browse & filter researchers by method, sector & rate',
                  'Shortlist, select and manage interested researchers',
                  'Direct messaging with researchers'
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-sm bg-[#E6F3F4] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="text-[#0D7377]" size={14} />
                    </div>
                    <span className="text-neutral-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-24 sm:py-32 bg-neutral-900 text-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6"
            style={{ fontFamily: 'Outfit' }}
          >
            Ready to Get Started?
          </h2>
          <p className="text-lg text-neutral-400 mb-10 max-w-2xl mx-auto">
            Join researchers, agencies and clients already using Research Exchange 
            to discover, commission and deliver market research — from quick gigs to complex engagements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="cta-researcher-btn"
              onClick={() => login('researcher')}
              className="btn-researcher px-8 py-4 font-semibold text-lg flex items-center justify-center gap-2"
            >
              I'm a Researcher
              <ArrowRight size={20} />
            </button>
            <button
              data-testid="cta-client-btn"
              onClick={() => login('client')}
              className="btn-client px-8 py-4 font-semibold text-lg flex items-center justify-center gap-2"
            >
              I'm a Client
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-neutral-900 rounded-sm flex items-center justify-center text-white font-bold text-xs">
              RE
            </div>
            <span className="font-semibold" style={{ fontFamily: 'Outfit' }}>
              Research Exchange
            </span>
          </div>
          <p className="text-sm text-neutral-500">
            © 2026 Research Exchange. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
